import React, { useState, useEffect } from "react";
import { notification, Row, Col, Spin, Modal } from "antd";
import MaskedInput from "antd-mask-input";
import moment from "moment";
import api from "../../services/api";
import { onlyNumbers } from "../../services/onlyNumber";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { decodeToken, logout } from "../../services/auth";

import {
  genderOptions,
  maritalStatus,
  scholarityOptions,
  atuationArea,
} from "../../models/franchiseeInfos";

import {
  Container,
  Content,
  Form,
  FormItem,
  Input,
  Button,
  Header,
  Logo,
  Select,
  DatePicker,
  ProfileImageContainer,
  ProfileImage,
  ProfileImageUploadOverlay,
  Alert,
  LogOutIcon,
  ButtonLogout,
} from "./styles";

import LogoImg from "../../assets/image/logo.png";
import Upload from "../../components/Upload";
import VerificationPhone from "../../components/VerificationPhone";
import AvatarUser from "../../assets/image/avatar_user.png";

interface IProps extends RouteComponentProps {}

const FirstAccessProfile: React.FC<IProps> = ({ history }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState<File | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [selectedScholarity, setSelectedScholarity] = useState<
    number | undefined
  >();
  const [isAnotherArea, setIsAnotherArea] = useState<string | undefined>();
  const [currentStep, setCurrentStep] = useState(0);
  const [pendingUserData, setPendingUserData] = useState<any>(null);

  useEffect(() => {
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const userInfo = decodeToken();

      if (!userInfo || !userInfo.id) {
        notification.error({
          message: "Erro de autenticação",
          description:
            "Não foi possível identificar o usuário. Por favor, faça login novamente.",
        });
        history.push("/login");
        return;
      }

      const response = await api.get(`/user/${userInfo.id}`);
      setUserData(response.data.content);
      setupForm(response.data.content);
    } catch (error) {
      console.error("Erro ao buscar dados do usuário:", error);
      notification.error({
        message: "Erro ao buscar dados",
        description: "Não foi possível carregar seus dados de perfil.",
      });
    } finally {
      setLoading(false);
    }
  };

  const isPhoneChanged = (newPhone: string) => {
    if (!userData?.phone) return true;
    const formattedNewPhone = onlyNumbers(newPhone)?.toString();
    const formattedOldPhone = userData.phone.toString();
    return formattedNewPhone !== formattedOldPhone;
  };

  const setupForm = (data: any) => {
    if (!data) return;

    setSelectedScholarity(data?.scholarity);
    setIsAnotherArea(data?.atuation);

    const formData = {
      ...data,
      country_code: data?.country_code || (data?.cpf ? "+55" : "+595"),
      birthday: data?.birthday ? moment(data?.birthday) : null,
    };

    form.setFieldsValue(formData);
  };

  const onFinish = async (values: any) => {
    try {
      setLoading(true);

      let payload = {
        ...values,
        first_access: false,
      };

      if (
        payload.atuation === "Outro (especificar)" &&
        payload.other_atuation
      ) {
        payload.atuation = payload.other_atuation;
        delete payload.other_atuation;
      }

      if (payload.birthday) {
        payload.birthday = moment(payload.birthday).set({
          hour: 12,
          minute: 0,
          second: 0,
        });
      }

      if (payload.phone) {
        payload.phone = onlyNumbers(payload.phone)?.toString();
      }

      if (
        payload.phone &&
        !payload.ruc &&
        !userData?.ruc &&
        isPhoneChanged(payload.phone)
      ) {
        setPendingUserData(payload);
        setCurrentStep(1);
        setLoading(false);
        return;
      }

      if (image) {
        const imageToUpload = new FormData();
        imageToUpload.append("file", image as File);

        const {
          data: { location, key },
        } = await api.post("/s3-upload/upload", imageToUpload);

        payload.image = location;
        payload.image_key = key;
      }

      const userId = userData?.id || decodeToken().id;

      await api.put(`/user/${userId}`, payload);

      localStorage.setItem("first_access", "false");

      notification.success({
        message: "Perfil atualizado com sucesso",
        description: "Agora você pode acessar o sistema completo",
        duration: 3,
      });

      window.location.href = "/";
    } catch (err: any) {
      console.error("Error in onFinish:", err);
      let errorMessage = "Um erro ocorreu ao salvar seus dados";

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      notification.warning({
        message: "Erro ao atualizar perfil",
        description: errorMessage,
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    Modal.confirm({
      title: "Tem certeza de que deseja sair?",
      okText: "Sim",
      centered: true,
      okType: "primary",
      cancelText: "Não",
      width: 500,
      async onOk() {
        logout();
        history.push("/login");
      },
    });
  };

  const completeUpdate = async () => {
    if (!pendingUserData) return;

    try {
      setLoading(true);

      if (image) {
        const imageToUpload = new FormData();
        imageToUpload.append("file", image as File);

        const {
          data: { location, key },
        } = await api.post("/s3-upload/upload", imageToUpload);

        pendingUserData.image = location;
        pendingUserData.image_key = key;
      }

      pendingUserData.pin_phone = null;

      const userId = userData?.id || decodeToken().id;

      await api.put(`/user/${userId}`, pendingUserData);

      localStorage.setItem("first_access", "false");

      notification.success({
        message: "Perfil atualizado com sucesso",
        description: "Agora você pode acessar o sistema completo",
        duration: 3,
      });

      window.location.href = "/";
    } catch (err: any) {
      let errorMessage = "Um erro ocorreu ao salvar seus dados";

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      notification.warning({
        message: "Erro ao atualizar perfil",
        description: errorMessage,
        duration: 5,
      });
    } finally {
      setLoading(false);
      setCurrentStep(0);
      setPendingUserData(null);
    }
  };

  const handleBack = () => {
    setCurrentStep(0);
    if (pendingUserData) {
      form.setFieldsValue({
        ...pendingUserData,
        phone: pendingUserData.phone,
        birthday: pendingUserData.birthday
          ? moment(pendingUserData.birthday)
          : null,
      });
    } else if (userData) {
      setupForm(userData);
    }
  };

  return (
    <Container>
      <Content>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            width: "100%",
          }}
        >
          <ButtonLogout type="default" onClick={handleLogout}>
            <LogOutIcon />
            Sair
          </ButtonLogout>
        </div>
        <Header>
          <Logo src={LogoImg} />
          <h1>Complete seu perfil</h1>
          <p>Para continuar utilizando o sistema, atualize suas informações</p>
        </Header>

        {loading ? (
          <Spin size="default" />
        ) : currentStep === 0 ? (
          <Form layout="vertical" form={form} onFinish={onFinish}>
            <ProfileImageContainer>
              {image ? (
                <ProfileImage
                  src={URL.createObjectURL(image)}
                  alt="Imagem de perfil"
                />
              ) : (
                <ProfileImage
                  src={userData?.image || AvatarUser}
                  alt="Imagem de perfil"
                />
              )}
              <ProfileImageUploadOverlay>
                <Upload
                  file={image}
                  setFile={setImage}
                  isProfileImage={true}
                  typeFile="image/*"
                />
              </ProfileImageUploadOverlay>
            </ProfileImageContainer>

            {image && (
              <Alert
                message="A alteração da imagem de perfil será visível apenas após o próximo login."
                type="info"
                showIcon
              />
            )}

            <Row gutter={[12, 0]}>
              <Col lg={12} md={12} sm={24} xs={24}>
                <FormItem label="Nome" name="name">
                  <Input placeholder="Nome" />
                </FormItem>
              </Col>
              <Col lg={12} md={12} sm={24} xs={24}>
                <FormItem label="Sobrenome" name="last_name">
                  <Input placeholder="Sobrenome" />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[12, 0]}>
              <Col lg={12} md={12} sm={24} xs={24}>
                <FormItem label="Gênero" name="gender">
                  <Select
                    placeholder="Selecione o gênero"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {genderOptions.map((option) => (
                      <Select.Option value={option.value} key={option.value}>
                        {option.description}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>

              <Col lg={12} md={12} sm={24} xs={24}>
                <FormItem label="Data de nascimento" name="birthday">
                  <DatePicker
                    placeholder="Data de nascimento"
                    name="birthday"
                    format="DD/MM/YYYY"
                    locale={undefined}
                    inputReadOnly={false}
                    allowClear={true}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[12, 0]}>
              <Col lg={4} md={4} sm={8} xs={8}>
                <FormItem
                  label="Código"
                  name="country_code"
                  rules={[{ required: true, message: "Código é obrigatório" }]}
                >
                  <Select>
                    <Select.Option value="+55">+55</Select.Option>
                    <Select.Option value="+595">+595</Select.Option>
                  </Select>
                </FormItem>
              </Col>
              <Col lg={8} md={8} sm={16} xs={16}>
                <FormItem
                  label="Telefone"
                  name="phone"
                  rules={[
                    {
                      required: true,
                      message: "Telefone é obrigatório",
                    },
                  ]}
                >
                  <MaskedInput
                    style={{
                      border: "1px solid var(--gray-100)",
                      borderRadius: "0.3rem",
                      height: "2.7rem",
                    }}
                    name="phone"
                    mask="(11) 11111-1111"
                    placeholder="(99) 99999-9999"
                  />
                </FormItem>
              </Col>
              <Col
                lg={12}
                md={12}
                sm={24}
                xs={24}
                style={{ marginTop: "0.21rem" }}
              >
                <FormItem label="Grau de escolaridade" name="scholarity">
                  <Select
                    placeholder="Selecione o grau de escolaridade"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={(value) =>
                      setSelectedScholarity(value as number | undefined)
                    }
                    dropdownRender={(menu) => menu}
                  >
                    {scholarityOptions.map((option) => (
                      <Select.Option value={option.value} key={option.value}>
                        {option.description}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
            </Row>

            {selectedScholarity && selectedScholarity >= 6 ? (
              <Row gutter={[12, 0]}>
                <Col lg={24} md={24} sm={24} xs={24}>
                  <FormItem label="Cursos/Graduações" name="courses">
                    <Input
                      placeholder="Informe seus cursos/graduações"
                      style={{
                        border: "1px solid var(--gray-100)",
                        borderRadius: "0.3rem",
                        height: "2.7rem",
                      }}
                    />
                  </FormItem>
                </Col>
              </Row>
            ) : null}

            <Row gutter={[12, 0]}>
              <Col lg={12} md={12} sm={24} xs={24}>
                <FormItem label="Estado civil" name="marital_status">
                  <Select
                    placeholder="Selecione o estado civil"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {maritalStatus.map((option) => (
                      <Select.Option value={option.value} key={option.value}>
                        {option.description}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>

              <Col lg={12} md={12} sm={24} xs={24}>
                <FormItem label="Atuação" name="atuation">
                  <Select
                    placeholder="Selecione a área de atuação"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={(value) => setIsAnotherArea(value as string)}
                  >
                    {atuationArea.map((option) => (
                      <Select.Option
                        value={option.description}
                        key={option.value}
                      >
                        {option.description}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
            </Row>

            {isAnotherArea === "Outro (especificar)" && (
              <Row gutter={[12, 0]}>
                <Col lg={24} md={24} sm={24} xs={24}>
                  <FormItem
                    label="Outra área de atuação"
                    name="other_atuation"
                    rules={[
                      {
                        required: true,
                        message: "A atuação é obrigatória",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Digite a área de atuação"
                      maxLength={50}
                      style={{
                        border: "1px solid var(--gray-100)",
                        borderRadius: "0.3rem",
                        height: "2.7rem",
                      }}
                    />
                  </FormItem>
                </Col>
              </Row>
            )}

            <Button htmlType="submit" type="primary" loading={loading}>
              Salvar e continuar
            </Button>
          </Form>
        ) : (
          <VerificationPhone
            userId={userData?.id || decodeToken().id}
            phoneNumber={
              pendingUserData?.phone
                ? pendingUserData.phone
                : onlyNumbers(form.getFieldValue("phone") || "")?.toString()
            }
            onVerified={completeUpdate}
            onCancel={handleBack}
            title="Verificação de Número de Telefone"
            showCancelConfirmation={true}
          />
        )}
      </Content>
    </Container>
  );
};

export default withRouter(FirstAccessProfile);
